import React, { useEffect, useCallback } from "react";
import { usePrevious } from "../../hooks";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import { Delete as DeleteIcon, Send as SendIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Field } from "react-final-form";

import { TextField } from "../common/Fields";
import { Content } from "../layout";
import {
  getGateway,
  submitGateway,
  updateGateway,
  deleteGateway,
  fetchOrgs,
} from "../../actions";

const GatewayForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { gateway } = useSelector((state) => state.gateway);
  const { user } = useSelector((state) => state.auth);
  const prevOrganization = usePrevious(user?.Organization?.id);

  useEffect(() => {
    dispatch(fetchOrgs({}));
    if (id) {
      dispatch(getGateway(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (user && prevOrganization) {
      if (user.Organization.id !== prevOrganization) {
        navigate("/gateways");
      }
    }
  }, [user, prevOrganization, navigate]);

  const onSubmit = useCallback(
    async (gateway) => {
      const upsertGateway = new Promise((resolve, reject) => {
        if (id) {
          dispatch(updateGateway({ gateway, id }))
            .then(() => resolve())
            .catch((error) => reject(error));
        } else {
          dispatch(submitGateway({ gateway }))
            .then(() => resolve())
            .catch((error) => reject(error));
        }
      });

      try {
        await upsertGateway;
        navigate("/gateways");
      } catch (error) {
        let errors;
        if (error.response.data?.errors) {
          const arrayErrors = error.response.data.errors;
          arrayErrors.forEach((error) => {
            errors = { ...errors, [error.param]: error.msg };
          });
        }
        return errors;
      }
    },
    [id, dispatch, navigate]
  );

  const onDelete = useCallback(
    async (e) => {
      e.preventDefault();
      if (window.confirm("삭제하시겠습니까?")) {
        dispatch(deleteGateway({ id }, navigate))
          .then(() => navigate("/gateways"))
          .catch((error) => alert(error));
      }
    },
    [id, dispatch, navigate]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        OrganizationId: gateway?.Organization?.id ?? user?.Organization?.id,
        LoRaGatewayId: gateway?.LoRaGatewayId,
        name: gateway?.name,
        description: gateway?.description,
      }}
      validate={({ name, description }) => {
        const errors = {};
        if (!name) {
          errors.name = "필수";
        }
        if (!description) {
          errors.description = "필수";
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <Content>
          <form onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  게이트웨이
                </Typography>
                <Grid
                  container
                  alignItems="flex-start"
                  spacing="20"
                  sx={{ mt: 2 }}
                >
                  <Grid item xs={12}>
                    {user && (
                      <Box>
                        <InputLabel shrink={true}>건설사</InputLabel>
                        <Box>
                          {gateway?.Organization?.displayName ??
                            user?.Organization?.displayName}
                        </Box>
                        <Field
                          name="OrganizationId"
                          type="hidden"
                          render={({ input, meta }) => <input {...input} />}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="LoRaGatewayId"
                      type="text"
                      label="게이트웨이 ID"
                      disabled={id ? true : false}
                      helperText="HEX 문자열만 입력할 수 있습니다."
                      inputProps={{
                        pattern: "[0-9a-fA-F]{16}",
                        maxLength: "16",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="name"
                      type="text"
                      label="구분값"
                      helperText="영문자, 숫자, '-'만 가능합니다."
                      inputProps={{
                        pattern: "[\\w-]+",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="description"
                      type="text"
                      label="이름"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {submitError && <FormHelperText>{submitError}</FormHelperText>}
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={6}>
                {id && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                  >
                    삭제
                  </Button>
                )}
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  type="submit"
                >
                  저장
                </Button>
              </Grid>
            </Grid>
          </form>
        </Content>
      )}
    />
  );
};

export default GatewayForm;
